import { useEffect, useRef } from 'react';

import { config } from '@/forms/config';
import { useFormActions, useFormState } from '@/forms/core';
import type { FieldsGroupRestrictionPayload, FormField } from '@/forms/typings';
import { FormFieldType } from '@/forms/typings';
import { atLeastOneChildAnsweredYesKey } from '@/forms/utils/createAtLeastOneChildAnsweredYesYup';
import {
  atLeastOneChildWithValueChildren,
  atLeastOneChildWithValueKey,
} from '@/forms/utils/createAtLeastOneChildWithValueYup';
import { limitRuleKey, readLimitAmount } from '@/forms/utils/createLimitPairsYup';
import { formFieldHasFormikValue } from '@/forms/utils/formFieldHasFormikValue';

/**
 * True once every question an AtLeastOneChildAnsweredYes rule reads has been resolved —
 * answered either way, or marked Not Applicable. Pure so it can be unit tested without a
 * Formik/React render tree.
 */
export const everyRuleQuestionAnswered = (
  children: FormField[],
  values: Record<string, unknown>,
): boolean => {
  // Only questions can satisfy the rule, so only questions decide when it can be judged.
  const questions = children.filter(
    child => child.type === FormFieldType.QuestionWithDetails,
  );

  return (
    questions.length > 0 &&
    questions.every(question =>
      formFieldHasFormikValue(question, values, {
        treatNotApplicableAsValue: true,
      }),
    )
  );
};

/**
 * Rule keys of every limit-pair amount holding an amount its rule can already judge.
 *
 * Read from `values`, never from `touched`: a reinitialize empties `touched` while the
 * stored amounts stay on screen, and the message has to come back without another submit.
 */
export const judgeableLimitRuleKeys = (
  groupFieldId: string,
  values: Record<string, unknown>,
  restriction?: FieldsGroupRestrictionPayload,
): string[] => {
  const pairs = restriction?.limitPairs?.pairs;

  if (!pairs) return [];

  const keys: string[] = [];

  for (const pair of pairs)
    for (const itemID of [pair.lowerItemID, pair.upperItemID]) {
      const amountFieldId = `${groupFieldId}_${itemID}`;

      // A blank amount is the provider's starting state and an unparseable one is not an
      // ordering failure — neither has anything to say until they press Continue.
      if (readLimitAmount(values[amountFieldId]) === undefined) continue;

      keys.push(limitRuleKey(amountFieldId));
    }

  return keys;
};

/** Each upload child's file-count key with the number of files it holds now. */
export const uploadCounts = (
  children: FormField[],
  values: Record<string, unknown>,
): Record<string, number> =>
  Object.fromEntries(
    children
      .filter(child => child.type === FormFieldType.File)
      .map(child => {
        const key = `${child.fieldId}${config.minFilesCountFieldSuffix}`;

        return [key, Number(values[key]) || 0];
      }),
  );

/**
 * True once the provider has left a child an AtLeastOneChildWithValue rule reads: been in
 * and out of one of its text boxes, or removed the last file from one of its uploads.
 */
export const ruleChildLeft = (
  children: FormField[],
  touched: Record<string, boolean>,
  counts: Record<string, number>,
  previousCounts: Record<string, number>,
): boolean =>
  children.some(
    child => child.type !== FormFieldType.File && Boolean(touched[child.fieldId]),
  ) ||
  Object.entries(counts).some(
    ([key, count]) => count === 0 && (previousCounts[key] ?? 0) > 0,
  );

/**
 * Touches a group's synthetic rule keys once their rule can be judged.
 *
 * These keys are synthetic — no input writes to them — so without this nothing touches
 * them until submit touches every key, and a rule stays silent on blur, on returning to a
 * section that already holds a failing combination, and after every autosave, whose
 * reinitialize empties `touched` under a summary the provider is still reading.
 *
 * An AtLeastOneChildWithValue rule that lists an upload is judged when the provider leaves its
 * children, which only `touched` records, so its trigger is latched to outlive the reinitialize
 * that empties it. Any other AtLeastOneChildWithValue rule waits for Continue.
 */
export const useGroupRestrictionTouch = (
  groupFieldId: string,
  children: FormField[] | undefined,
  restriction?: FieldsGroupRestrictionPayload,
): void => {
  const { values, touched } = useFormState();
  const { setFieldTouched } = useFormActions();

  const answeredYesKey = restriction?.atLeastOneChildAnsweredYes
    ? atLeastOneChildAnsweredYesKey(groupFieldId)
    : undefined;

  const answered =
    answeredYesKey !== undefined &&
    everyRuleQuestionAnswered(children ?? [], values);

  const withValue = restriction?.atLeastOneChildWithValue;
  const withValueKey = withValue
    ? atLeastOneChildWithValueKey(groupFieldId)
    : undefined;
  const ruleChildren = withValue
    ? atLeastOneChildWithValueChildren(groupFieldId, children ?? [], withValue)
    : [];

  const counts = uploadCounts(ruleChildren, values);
  // Only an explanation-or-document pair reports on leaving; other rules wait for Continue.
  const listsUpload = ruleChildren.some(
    child => child.type === FormFieldType.File,
  );

  // A removal touches nothing, so comparing with the last render's counts is what reveals it.
  const previousCounts = useRef(counts);
  // Latched because a reinitialize empties `touched`, the only record of leaving a text box.
  const leftBefore = useRef(false);
  const left =
    withValueKey !== undefined &&
    listsUpload &&
    (leftBefore.current ||
      ruleChildLeft(ruleChildren, touched, counts, previousCounts.current));

  useEffect(() => {
    previousCounts.current = counts;
    if (left) leftBefore.current = true;
  });

  const pending = [
    ...(answeredYesKey && answered ? [answeredYesKey] : []),
    ...(withValueKey && left ? [withValueKey] : []),
    ...judgeableLimitRuleKeys(groupFieldId, values, restriction),
  ].filter(key => !touched[key]);

  // Joined so the effect depends on *which* keys are pending rather than on the identity
  // of a list rebuilt every render — Formik hands back a fresh `values` on each keystroke.
  // Safe as a separator: a field id is a path of item ids and never holds a space.
  const pendingKeys = pending.join(' ');

  useEffect(() => {
    if (!pendingKeys) return;

    for (const key of pendingKeys.split(' ')) setFieldTouched(key, true, true);
  }, [pendingKeys, setFieldTouched]);
};
